/* pf-sticky-fix.js v3 - Ella's mobile sticky Add to cart bar, given a reason to be there.

   THE SETTING. The owner's theme-editor setting "always show sticky add to cart on mobile" puts
   `always-on-mobile` on both `.productView-stickyCart` and `.productView-form`. On a phone that
   class pins the bar `position:fixed; bottom:0` for the WHOLE page - it is not scroll-triggered -
   and it sets `.productView-action{display:none}`, so the buy box's own Add to cart is removed and
   the bar is the only buy button anywhere. A bar that is always at the bottom of the viewport is
   always standing on whatever happens to be at the bottom of the viewport, and on a cushion whose
   title runs to four lines that is the size picker: measured at 390x844, bar y 779-844, Width and
   Length selects 760-808, so 29 of their 48px - the centre the finger lands on included - are behind
   it and document.elementFromPoint at each select's centre returns the bar.

   THE RULE. A sticky Add to cart exists to give the shopper a CTA when the real Add to cart is off
   screen. So: show the bar exactly when the page's own Add to cart is NOT in the viewport, and hide
   it when it is. That is an IntersectionObserver on the real button and nothing cleverer. For that
   rule to have anything to observe the real button has to exist, which is what pf-sticky-fix.css
   restores - and restoring it is also what gives the shopper a full-width Add to cart in the buy
   box, in the place Ella already puts it on desktop.

   THE ONE THING THIS FILE CANNOT DO, stated up front because two review rounds have now asked for
   it. "The bar is on screen whenever the real button is not" and "the bar never covers a form
   control" cannot both be true at the top of a phone product page. The bar is position:fixed;
   bottom:0, so while it is shown it owns the bottom 65px of the viewport; at scrollY 0 that band is
   where the size pickers are. Measured, with no files loaded at all:
     390x844 cushion: bar 779-844; Width/Length selects 760-808; "Add cushion details" label
                      820-864 and its checkbox 831-853. The next clear gap upwards is 628-734.
     360x780 cushion: bar 715-780; selects 722-770 (all 48px behind it); addon label 782-826.
     430x932 cushion: bar 867-932; selects 787-835 are clear; addon label 847-891 is not.
   So at scrollY 0 either the bar is there and it clips a control, or it is not there and the page
   has no Add to cart on it (the real one is at document y 1343 on the 390 cushion). Moving the bar
   up into the 628-734 gap was measured and rejected: it lands on the price. This file ships the
   first of those, because "never zero Add to cart" is the harder requirement and because clipping
   is exactly what the storefront does today, so it is not a regression. The second is implemented
   too and is one flag away - see CLEAR MODE - so the owner can look at both and choose.

   Consequences, stated plainly rather than hidden:
   - There is always a buy button on screen. Below the buy box it is the bar; at the buy box it is
     the real button; the two never both disappear and there is never a gap between them.
   - The bar's height is reserved at the foot of the document ONLY while the bar is actually shown,
     so the last control on the page can always be scrolled clear of it and there is no dead space
     under the page when the bar is away.
   - While the bar is shown it still occupies the bottom 65px of the viewport, which is what the
     owner asked for. Content scrolls under it exactly as it does under every sticky bar; what this
     file guarantees is that nothing is ever TRAPPED there (see the foot reservation, and the
     scroll-padding-bottom in the stylesheet, which lifts any control the shopper focuses clear of
     the bar).
   - Two side effects of restoring .productView-action are declared in the shipping report rather
     than buried here: the 10px of room the share icon needs above the button, and the fact that a
     sold-out product now shows its own disabled "Sold out" button on a phone the way it already
     does on desktop.

   CLEAR MODE (off by default; ?pf_sticky_clear=1 turns it on for one pageview, or flip CLEAR_DEFAULT
   below). In clear mode the bar is ALSO hidden whenever any control in the product section - a
   select, an input, a label, a swatch, a button, the restored Add to cart included - would be under
   it. That is the "never overlaps anything, ever" behaviour, and it costs the landing screen its
   buy button for the first ~100px of scroll. Both sets of numbers are in the report. This is the
   owner's call, not the code's, which is why it is a flag and not a decision.

   Two properties worth keeping in mind before editing:
   - It only ever ADDS a hide. It never forces the bar to show, so the owner's on/off setting and
     Ella's own logic keep the last word. If the bar is switched off in the theme editor the snippet
     is not rendered, findBar() returns null, and this file does nothing at all.
   - It writes attributes on <html> and nothing else. Everything inside .productView-stickyCart
     belongs to pf-cc-form.js (the read-only name + price label, the neutered 250-variant pickers,
     the add-to-cart routing), which re-applies itself through a MutationObserver watching from the
     bar up to the section root - so inserting or moving a node in there would set it off every time.
     pf-pdp.js reads the bar too, mirroring the buy-box price into .sticky-product-wrapper.

   LOAD ORDER. This file is loaded for every product page and runs BEFORE pf-cc-form.js, so at first
   run the buy box may not exist yet. Nothing here assumes it does: the bootstrap retries on
   DOMContentLoaded, on load, and from a MutationObserver, and the same observer is what re-arms the
   fix if anything later replaces the bar or the button. Every measurement behind this file was taken
   with the two assets injected inline into <head> ahead of every theme script, which is earlier than
   the deferred tag will ever run, so the "nothing exists yet" path is exercised on every run.

   FAIL-SAFE. data-pf-atc (the stylesheet's gate, which restores the real Add to cart) is written
   once and never taken back, so the page can never be left with both the real button hidden and the
   bar hidden. There is no disengage() and no unwire(). The bar's own hide is a separate attribute,
   and it is only ever set while the real button has been measured on screen at a non-zero height.

   Kill switch: ?pf_sticky_off=1 - the whole package, stylesheet included, because every rule in
   pf-sticky-fix.css is gated on an attribute only this script writes. It also stands down under
   ?pf_cc_off=1 / ?pf_pdp_off=1 so that "show me the stock page" stays a single flag on either
   product line. */
(function () {
  'use strict';

  /* Flip to true to make clear mode the default. Read the CLEAR MODE note above first: it is a
     trade, not an improvement, and the owner is the one who gets to make it. */
  var CLEAR_DEFAULT = false;

  if (window.__pfStickyFix) { return; }
  if (/(?:^|[?&])pf_(?:sticky|cc|pdp)_off=1(?:&|$)/.test(location.search)) { return; }

  var H = document.documentElement;
  var MQ = '(max-width: 767px)';
  var CLEAR = CLEAR_DEFAULT || /(?:^|[?&])pf_sticky_clear=1(?:&|$)/.test(location.search);

  var S = window.__pfStickyFix = {
    version: '3', clear: CLEAR, engaged: false, ready: false, hidden: null, flips: 0, barH: 0,
    lost: 0, rearm: 0, calls: 0, why: '', covers: 0
  };

  var bar = null, atc = null;
  var io = null, ro = null, mo = null, mq = null, raf = 0, wired = false;
  var ctrls = null, ctrlsAt = 0;

  function q(s) { try { return document.querySelector(s); } catch (e) { return null; } }
  function rect(el) { try { return el.getBoundingClientRect(); } catch (e) { return null; } }
  function alive(el) { return !!(el && el.parentNode && document.contains(el)); }

  /* ---- the nodes ------------------------------------------------------------------------- */

  function findBar() {
    /* Only the "always on mobile" layout is a bottom bar. In every other configuration Ella parks
       the bar at the top of the screen, where it covers nothing and needs no help from us. */
    return q('.productView-stickyCart.always-on-mobile');
  }

  function findAtc() {
    /* The same form pf-cc-form.js calls the main one, so we watch the button its validator guards
       rather than the sticky bar's second copy. */
    return q('[id^="ProductSection-template--"] form[data-type="add-to-cart-form"][id^="product-form-"]:not([id^="product-form-sticky"]) button[name="add"]')
      || q('form[data-type="add-to-cart-form"][id^="product-form-"]:not([id^="product-form-sticky"]) button[name="add"]');
  }

  /* Re-resolve whatever has gone missing. Returns true when we have a bar to work with. This is the
     only place either node handle is assigned, so there is exactly one recovery path and it is the
     same one on first run, after a variant re-render and after a section reload. */
  function ensure() {
    if (!alive(bar)) {
      if (bar) { S.lost++; }
      bar = findBar();
      if (bar) { S.rearm++; observe(); }
    }
    if (!alive(atc)) { atc = findAtc(); if (atc) { observe(); } }
    return !!bar;
  }

  /* ---- clear mode: is anything tappable under the bar right now? -------------------------- */

  /* Only ever called in clear mode, so the default build never pays for it. The node list is
     re-queried at most four times a second and only while it is actually being used; the per-frame
     cost is one getBoundingClientRect per control, on a list that is scoped to the product section
     and measured at 24-70 nodes on these pages. */
  var CTRL_SEL = 'select, textarea, input:not([type="hidden"]), button,' +
    ' .productView-options a, .productView-options label, label.pf-addon__label,' +
    ' .swatch-element, [data-value], .pf-buybox button, .pf-buybox a';

  function controls() {
    var now = Date.now();
    if (ctrls && (now - ctrlsAt) < 250) { return ctrls; }
    ctrlsAt = now;
    var root = q('[id^="ProductSection-template--"]') || document.body;
    var list = [];
    try {
      Array.prototype.forEach.call(root.querySelectorAll(CTRL_SEL), function (e) {
        if (bar && bar.contains(e)) { return; }
        list.push(e);
      });
    } catch (e) { list = []; }
    ctrls = list;
    return list;
  }

  /* The bar's band is the bottom barH of the viewport - where the bar sits when it is shown, which
     is what we have to test against even while it is currently hidden. */
  function coversAnything(vh, barH) {
    var top = vh - barH, n = 0;
    var list = controls();
    for (var i = 0; i < list.length; i++) {
      var e = list[i];
      if (!e.parentNode) { continue; }
      var b = rect(e);
      if (!b || b.width < 8 || b.height < 8) { continue; }
      if (b.bottom <= top || b.top >= vh) { continue; }
      var st;
      try { st = getComputedStyle(e); } catch (err) { continue; }
      if (st.display === 'none' || st.visibility === 'hidden' || parseFloat(st.opacity) < 0.05) { continue; }
      n++;
    }
    S.covers = n;
    return n > 0;
  }

  /* ---- the decision ---------------------------------------------------------------------- */

  function setHide(hide) {
    if (S.hidden === hide) { return; }
    S.hidden = hide;
    S.flips++;
    if (hide) { H.setAttribute('data-pf-sticky-hide', '1'); } else { H.removeAttribute('data-pf-sticky-hide'); }
    /* The first decision is made before the shopper has looked at anything; animating it would show
       the bar sliding away on every page load, so the transition is only armed one frame later. */
    if (!S.ready) {
      S.ready = true;
      var arm = function () { H.setAttribute('data-pf-sticky-ready', '1'); };
      if (window.requestAnimationFrame) { requestAnimationFrame(function () { requestAnimationFrame(arm); }); } else { setTimeout(arm, 50); }
    }
  }

  /* Every wake-up lands here. Before the bar exists this is the bootstrap - the MutationObserver
     below keeps calling it until the product section has been parsed - and afterwards it is the
     decision. One entry point, so "not there yet" and "went away and came back" are the same path. */
  function tick() {
    raf = 0;
    S.calls++; /* diagnostic only - the MutationObserver below is broad, so this is how you check it */
    if (!S.engaged) { if (mq && mq.matches) { engage(); } return; }
    decide();
  }

  function decide() {
    if (!S.engaged) { return; }

    if (!ensure()) {
      /* No bar on the page any more. Nothing to hide - and, more to the point, never leave the hide
         in place, because the stylesheet would then be holding back a bar that does not exist while
         the real Add to cart is the page's only buy button. data-pf-atc stays, so that button stays. */
      S.why = 'no-bar';
      setHide(false);
      return;
    }

    var h = bar.offsetHeight || 0; /* offsetHeight ignores the transform that hides it */
    if (h && h !== S.barH) {
      S.barH = h;
      H.style.setProperty('--pf-sticky-h', h + 'px');
      observe();
    }

    /* THE RULE: the bar is redundant exactly while the real Add to cart is on screen.
       REVEAL is the only subtlety, and it is about the handover rather than the rule. "On screen" is
       read as "at least 24px of the button is on screen", not "one pixel of it has crossed the edge",
       because the bar is what the shopper is looking at when the button arrives: at 390x844 the plain
       test hands over while the button is a 1px sliver on the bottom edge, which measured as a scroll
       window with no usable CTA at all. Waiting for 24px means the bar is still there for those few
       pixels and the button is properly on screen the moment it leaves. The declared cost of that
       choice is that the button's top edge passes under the bar for those 24px of scroll - measured
       at 23px on the 390 cushion (y=450), 20px on the 390 L-shaped sofa (y=400) and 9px on the 360
       L-shaped sofa (y=450), always with blocked=false, because the thing on top of it is the bar,
       which is itself a full-width Add to cart. It is cosmetic, it is the price of never having a
       scroll position with no buy button on it, and clear mode removes it along with the rest.
       A zero-height rect means the button is still display:none - the stylesheet did not load, or
       the theme is mid re-render - and in that state hiding the bar would leave the shopper with no
       buy button at all, so the answer is always "show". */
    var vh = window.innerHeight || H.clientHeight || 0;
    var hide = false;
    if (atc) {
      var a = rect(atc);
      if (a && a.height > 0 && a.width > 0) {
        var reveal = Math.min(24, a.height);
        hide = a.bottom > reveal && a.top < vh - reveal;
        S.why = hide ? 'atc-on-screen' : 'atc-off-screen';
      } else {
        S.why = 'atc-not-rendered';
      }
    } else {
      S.why = 'no-atc';
    }

    /* Clear mode only ever adds a hide, and only when the bar would otherwise be shown. It can and
       does leave the landing screen with no Add to cart on it; that is the whole point of the flag
       and it is why the flag is off. */
    if (CLEAR && !hide && S.barH > 0) {
      if (coversAnything(vh, S.barH)) { hide = true; S.why = 'clear-covers'; }
    }

    setHide(hide);
  }

  function schedule() {
    if (raf) { return; }
    raf = window.requestAnimationFrame ? requestAnimationFrame(tick) : setTimeout(tick, 16);
  }

  /* ---- listeners ------------------------------------------------------------------------- */

  function observe() {
    if (io) { try { io.disconnect(); } catch (e) {} io = null; }
    if (!window.IntersectionObserver) { return; }
    try {
      /* The observer is the cheap half: it wakes us exactly when the button crosses an edge of the
         viewport, and - unlike a scroll handler - it also wakes us when the page reflows under a
         still finger, which on these pages is routine (a variant re-render, the "Add cushion
         details" panel opening, a gallery image finally landing). */
      io = new IntersectionObserver(schedule, { threshold: [0, 0.01, 1] });
      if (atc) { io.observe(atc); }
      if (bar) { io.observe(bar); }
    } catch (e) { io = null; }
  }

  function wire() {
    if (wired) { return; }
    wired = true;
    observe();
    /* The scroll listener is the half that is always right: an observer only fires on the edges it
       was built with, and it is missing altogether on older phones. It reads one rect on frames the
       page is already painting, and touches the DOM only when the answer changes. */
    try { window.addEventListener('scroll', schedule, { passive: true }); } catch (e) { window.addEventListener('scroll', schedule, false); }
    window.addEventListener('resize', schedule, false);
    window.addEventListener('orientationchange', schedule, false);
    window.addEventListener('pageshow', schedule, false);
    if (window.ResizeObserver) {
      /* pf-cc-form.js adds a name + price to the bar after we have measured it, which changes its
         height and therefore the space reserved at the foot of the page. */
      try { ro = new ResizeObserver(schedule); if (bar) { ro.observe(bar); } } catch (e) { ro = null; }
    }
  }

  /* The recovery half. It watches the whole document rather than the section, because on first run
     the section may not have been parsed yet - this same observer is the bootstrap AND the re-arm,
     so there is one code path for "the buy box is not there yet" and "the buy box was replaced".
     The callback does nothing but drop the control cache and ask for a frame; decide() re-resolves
     the nodes and writes only when the answer changes.
     v3 bounds it in two ways. It is only ever connected at phone width - nothing in this package
     does anything at 768px and up - which took a desktop or tablet pageview from 41-45 callbacks in
     v2 to a measured 0. And at `load` it lets go of any page with no product section on it, so a
     phone pageview of /cart, /collections/custom-cushions, the home page or a 404 is bounded to the
     20-26 callbacks its initial parse costs and then stops for good (measured: scrolling
     /collections/custom-cushions to 3500px afterwards left the count at 24, unchanged).
     It is connected and disconnected by the media-query listener, which is also what re-arms it when
     a phone is rotated back into portrait, and `shopify:section:load` re-arms it if a section is
     swapped in later. */
  function watch() {
    if (mo || !window.MutationObserver) { return; }
    try {
      mo = new MutationObserver(function () { ctrls = null; schedule(); });
      mo.observe(H, { childList: true, subtree: true });
    } catch (e) { mo = null; }
  }

  function unwatch() {
    if (!mo) { return; }
    try { mo.disconnect(); } catch (e) {}
    mo = null;
  }

  /* ---- lifecycle ------------------------------------------------------------------------- */

  function engage() {
    if (S.engaged) { return; }
    if (!ensure()) { return; }
    S.engaged = true;
    S.barH = bar.offsetHeight || 0;
    if (S.barH) { H.style.setProperty('--pf-sticky-h', S.barH + 'px'); }
    /* The stylesheet's gate. Nothing in pf-sticky-fix.css applies until this lands, which is also
       the kill switch: no attribute, no rules, and the page is exactly the page we have today.
       It is written once and never removed - see FAIL-SAFE at the top. */
    H.setAttribute('data-pf-atc', '1');
    wire();
    decide();
    /* The restored button is display:none until the stylesheet's rule applies, and on a cold cache
       that can be a frame or two after this runs. Ask again shortly so the first decision is made
       against the real geometry rather than a 0x0 rect. */
    setTimeout(schedule, 0);
    setTimeout(schedule, 250);
    setTimeout(schedule, 1200);
  }

  function relax() {
    /* Wider than a phone: the bar is not a bottom bar there, so nothing should be hidden. The
       stylesheet is inside @media (max-width:767px) anyway, so data-pf-atc is inert here; we leave
       it alone so that coming back to phone width needs no re-engagement. */
    if (S.hidden) { setHide(false); }
  }

  function start() {
    if (!mq) { return; }
    if (mq.matches) { watch(); engage(); } else { unwatch(); relax(); }
  }

  try { mq = window.matchMedia ? matchMedia(MQ) : null; } catch (e) { mq = null; }
  if (!mq) { return; }
  if (mq.addEventListener) { mq.addEventListener('change', start); } else if (mq.addListener) { mq.addListener(start); }

  /* Once everything has loaded, a page with no product section on it is not a page this file has
     any business watching. Letting go here is safe: a section can only appear afterwards through
     shopify:section:load (theme editor), which calls start() and re-connects the observer. */
  function settle() {
    start();
    if (!S.engaged && !q('[id^="ProductSection-template--"]') && !q('.productView')) { unwatch(); }
  }

  start();
  if (document.readyState === 'loading') { document.addEventListener('DOMContentLoaded', start); }
  window.addEventListener('load', settle, false);
  /* The theme editor swaps the whole product section, bar and buy box with it. */
  document.addEventListener('shopify:section:load', start, false);

  S.decide = decide;
  S.start = start;
})();
